import { DialogContent } from "@mui/material";
import React, { useState, useRef, useContext } from "react";
import {
  ColorPickerIcon,
  DocumentTextIcon,
  fileTypes,
  PolygonDottedLineOne,
  PolygonDottedLineThree,
  PolygonDottedLineTwo,
  PolygonLine,
  polygonLineTypes,
} from "../../../../../constants/WebMap";
import {
  ActionButtons,
  ColorInputWrapper,
  EmptyUploadMessage,
  ErrorMessage,
  FileContainer,
  FileItem,
  FileSupportNote,
  IconContainer,
  Input,
  IconSelection,
  InputGroup,
  Label,
  ModalContent,
  ModalHeader,
  ModalWrapper,
  UploadSection,
} from "./UploadPolygonModal.styled";
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { mapUploadFile } from "../../../../../services/api/mapFileUpload.api";
import { toast } from "react-toastify";
import { WebMapContext } from "../../../../../pages/sections/web-map/WebMapGoogleMap";
import { BackDrop } from "../../../Loader";

const icons = [
  { icon: <PolygonLine />, name: polygonLineTypes.POLYGON_LINE },
  { icon: <PolygonDottedLineOne />, name: polygonLineTypes.POLYGON_DOTTED_LINE_ONE},
  { icon: <PolygonDottedLineTwo />, name: polygonLineTypes.POLYGON_DOTTED_LINE_TWO },
  { icon: <PolygonDottedLineThree />, name: polygonLineTypes.POLYGON_DOTTED_LINE_THREE },
];

const UploadPolygonModal = ({ isOpen, onClose, onConfirm }) => {
  const { appliedFilters } = useContext(WebMapContext);

  const [color, setColor] = useState("#FF00FF");
  const [files, setFiles] = useState([]);
  const [name, setName] = useState("");
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [selectedIcon, setSelectedIcon] = useState(icons[0]);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState({file:'',name:''});
  const maxFileSize = 10 * 1024 * 1024;
  const maxFileCount = 7;

  const MANDATORY_SHAPE_FILES = [".shp", ".shx", ".dbf", ".prj", ".cpg"];
  const OPTIONAL_SHAPE_FILES = [".sbx", ".sbn"];
  const MANDATORY_TAB_FILES = [".tab", ".dat", ".map", ".id"];
  const OPTIONAL_TAB_FILES = [".ind", ".prj"];
  const VALID_FILE_TYPES = [
    ".shp",
    ".tab",
    ".kml",
    ...MANDATORY_SHAPE_FILES,
    ...MANDATORY_TAB_FILES,
    ...OPTIONAL_SHAPE_FILES,
    ...OPTIONAL_TAB_FILES,
  ];

  const fileInputRef = useRef(null);

  const validateFiles = (selectedFiles) => {
    setError({ file: "", name: "" });

    const extensions = selectedFiles.map((file) =>
      file.name.slice(file.name.lastIndexOf(".")).toLowerCase()
    );

    const existingExtensions = files.map((file) =>
      file.name.slice(file.name.lastIndexOf(".")).toLowerCase()
    );

    const allExtensions = [...extensions, ...existingExtensions];
    if (
      !allExtensions.includes(".shp") &&
      !allExtensions.includes(".tab") &&
      files.length + selectedFiles.length > 1
    ) {
      toast.error("Only one file permitted for this file type!", {
        theme: "colored",
      });
      return false;
    }

    for (const ext of extensions) {
      if (existingExtensions.includes(ext)) {
        toast.error(`Duplicate file with extension ${ext} detected.`, {
          theme: "colored",
        });
        return false;
      }
    }

    // Check file count
    if (files.length + selectedFiles.length > maxFileCount) {
      setError({
        ...error,
        file: "You have exceeded the maximum file upload limit. Please check your file size / File Count and try again.",
      });
      return false;
    }

    // Check for valid file types and sizes
    for (const file of selectedFiles) {
      const ext = file.name.slice(file.name.lastIndexOf(".")).toLowerCase();
      if (!VALID_FILE_TYPES.includes(ext)) {
        toast.error(`Invalid file type: ${file.name}`, { theme: "colored" });
        return false;
      }
      if (file.size > maxFileSize) {
        setError({
          ...error,
          file: "You have exceeded the maximum file upload limit. Please check your file size / File Count and try again.",
        });
        toast.error(`File too large: ${file.name}`, { theme: "colored" });
        return false;
      }
    }

    return true;
  };

  const validateMandatoryFiles = () => {
    const extensions = files.map((file) =>
      file.name.slice(file.name.lastIndexOf(".")).toLowerCase()
    );

    // Validate mandatory shape files
    if (extensions.includes(".shp")) {
      for (const ext of MANDATORY_SHAPE_FILES) {
        if (!extensions.includes(ext)) {
          setError({
            ...error,
            file: "Insufficient data uploaded to complete the task. Please upload all the required supporting files.",
          });
          return false;
        }
      }
    }

    // Validate mandatory tab files
    if (extensions.includes(".tab")) {
      for (const ext of MANDATORY_TAB_FILES) {
        if (!extensions.includes(ext)) {
          setError({
            ...error,
            name: "Insufficient data uploaded to complete the task. Please upload all the required supporting files.",
          });
          return false;
        }
      }
    }

    return true;
  };

  const handleFileUpload = (e) => {
    const selectedFiles = Array.from(e.target.files);
    if (validateFiles(selectedFiles)) {
      setFiles((prev) => [...prev, ...selectedFiles]);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const droppedFiles = Array.from(e.dataTransfer.files);
    if (validateFiles(droppedFiles)) {
      setFiles((prev) => [...prev, ...droppedFiles]);
    }
  };

  const removeFile = (indexToRemove, event) => {
    event.stopPropagation();
    setFiles((prev) => prev.filter((_, index) => index !== indexToRemove));
  };

  const toggleColorPicker = () => {
    setShowColorPicker(!showColorPicker);
  };

  const handleColorChange = (e) => {
    setColor(e.target.value);
  };

  const handleUpload = async () => {
    if (!files.length) {
      setError({...error,file:'Please upload at least one file to continue.'});
      return;
    }

    if (!name) {
      setError({...error,name:'Please Enter a File Display Name.'});
      return;
    }

    if (!validateMandatoryFiles()) {
      return;
    }

    const formData = new FormData();
    files.forEach((file) => formData.append("files", file));
    formData.append("color", color);
    formData.append("fileName", name);
    formData.append("icon", selectedIcon?.name);
    formData.append("country", appliedFilters.country);
    formData.append("year", appliedFilters.year);
    formData.append("type", fileTypes.POLYGON);
    formData.append("quarter", appliedFilters.quarter);

    setIsUploading(true);

    try {
      const response = await mapUploadFile(formData);

      if (!response?.data) {
        throw new Error("Failed to upload file. Please try again.");
      }

      toast.success(`Upload successful`, { theme: "colored" });
      onConfirm();
    } catch (error) {
      if (error?.response) {
        setError({ ...error, file: error?.response?.data?.message });
      }
      toast.error("Error uploading file. Please try again.", { theme: "colored" });
    } finally {
      setIsUploading(false);
    }
  };

  if (!isOpen) return null;

  const handleChange = (e) => {
    const regex = /^[a-zA-Z0-9_.\s-]*$/; 
    if (regex.test(e.target.value)) {
      setName(e.target.value);
    }
  };

  const Error = ({ error }) => {
    return (
      <div style={{display: "flex", flexDirection: "row", alignItems: "center"}}>
        <InfoOutlinedIcon
          style={{ color: "#E80000", fontSize: 17, margin: "4px" }}
        />
        <ErrorMessage>{error}</ErrorMessage>
      </div>
    );
  };

  const LabelComponent = ({ text }) => {
    return (
      <Label>{text}<span style={{color: "#FF0000", marginLeft: "4px"}}>*</span></Label>
    );
  };

  return (
    <>
      {isUploading ? <BackDrop /> : null}
      <ModalWrapper>
        <ModalContent>
          <ModalHeader>
            <span>Upload Map Data - Polygon</span>
            <button onClick={onClose}>&times;</button>
          </ModalHeader>
          <DialogContent dividers>
            <UploadSection
              onClick={() =>
                fileInputRef.current && fileInputRef.current.click()
              }
              onDragOver={handleDragOver}
              onDrop={handleDrop}
            >
              <input
                ref={fileInputRef}
                type="file"
                onChange={handleFileUpload}
                accept={VALID_FILE_TYPES.join(",")}
                multiple
              />
              {files.length === 0 ? (
                <EmptyUploadMessage>
                  Drop file or click here to browse...
                </EmptyUploadMessage>
              ) : (
                <FileContainer>
                  {files.map((file, index) => (
                    <FileItem key={index}>
                      <DocumentTextIcon
                        style={{
                          width: "16px",
                          height: "16px",
                          color: "#007842",
                        }}
                      />
                      <span>{file.name}</span>
                      <button onClick={(e) => removeFile(index, e)}>
                        &times;
                      </button>
                    </FileItem>
                  ))}
                </FileContainer>
              )}
            </UploadSection>
            <FileSupportNote>
              Supports only .shp, .tab, .kml files. / Max File Size
              : 10MB
            </FileSupportNote>
            <div>{error?.file ? <Error error={error?.file} /> : null}</div>
            <InputGroup>
              <LabelComponent text={'File Display Name'}/>
              <ColorInputWrapper>
                <Input
                  type="text"
                  value={name}
                  maxLength={50}
                  onChange={handleChange}
                  placeholder="Enter Name"
                />
              </ColorInputWrapper>
              <FileSupportNote>
                Enter Files display name for uploaded documents
              </FileSupportNote>
              <div>{error?.name ? <Error error={error?.name} /> : null}</div>
            </InputGroup>
            <InputGroup>
              <LabelComponent text={'Color:'}/>
              <ColorInputWrapper show={showColorPicker}>
                <Input
                  type="text"
                  value={color}
                  onChange={(e) => setColor(e.target.value)}
                  placeholder="eg: #FF00FF"
                />
                <button onClick={toggleColorPicker}>
                  <ColorPickerIcon />
                </button>
                <input
                  type="color"
                  id="colorPicker"
                  value={color}
                  onChange={handleColorChange}
                />
              </ColorInputWrapper>
            </InputGroup>
            <InputGroup>
            <LabelComponent text={"Line Style:"}/>
            <IconSelection>
              {icons.map((item, index) => (
                <label key={index}>
                  <input
                    type="radio"
                    name="icon"
                    value={item}
                    checked={selectedIcon === item}
                    onChange={() => setSelectedIcon(item)}
                  />
                  <IconContainer className="icon">
                    {React.cloneElement(item.icon, {
                      fill: selectedIcon === item ? "#007842" : "#707070",
                      stroke: selectedIcon === item ? "#007842" : "#707070",
                    })}
                  </IconContainer>
                </label>
              ))}
            </IconSelection>

            </InputGroup>
          </DialogContent>
          <ActionButtons>
            <button className="cancel" onClick={onClose}>
              Cancel
            </button>
            <button
              className="confirm"
              onClick={handleUpload}
              disabled={isUploading}
            >
              {"Confirm and upload"}
            </button>
          </ActionButtons>
        </ModalContent>
      </ModalWrapper>
    </>
  );
};

export default UploadPolygonModal;
